// Bộ máy hiệu ứng theo mùa (canvas 2D, không thư viện ngoài).
//   · Engine: danh sách hạt + nhân vật, mỗi khung step(dt) rồi draw().
//   · AMB: hạt nền theo dịp (hoa mai, lá, tuyết, bong bóng, tim, dơi…) — rải sẵn khắp màn lúc đầu.
//   · STAGE: "sân khấu" trong thẻ chào / nền: biển (sóng + cá heo + người lướt sóng),
//     bầu trời (xe tuần lộc, phù thủy + dơi, thần Cupid, đàn chim + đèn trời mùa thu).
//   · BURST: hiệu ứng khi tạo đơn thành công.
// Sprite tĩnh vẽ sẵn một lần vào canvas nhỏ; nhân vật động vẽ lại mỗi khung.

import type { SeasonKey } from '@shoptrack/common/season'

const TAU = Math.PI * 2
export const R = (a: number, b: number) => a + Math.random() * (b - a)
const pick = <T,>(a: readonly T[]): T => a[(Math.random() * a.length) | 0]!

export interface Particle {
  kind: 'spr' | 'rect' | 'spark' | 'rocket' | 'act'
  x: number
  y: number
  vx: number
  vy: number
  rot: number
  t: number
  k: number
  g?: number
  drag?: number
  sa?: number
  sf?: number
  ph?: number
  vr?: number
  fl?: number
  tw?: number
  life?: number
  ml?: number
  A?: number
  fade?: boolean
  dead?: boolean
  delay?: number
  amb?: boolean
  dir?: number
  spr?: HTMLCanvasElement
  s?: number
  c?: string
  sz?: number
  hist?: number[]
  // pháo hoa
  ty?: number
  cols?: readonly string[]
  n?: number
  pow?: number
  // nhân vật
  upd?: (e: Engine, q: Particle, dt: number) => void
  drw?: (g: CanvasRenderingContext2D, q: Particle, e: Engine) => void
  x0?: number
  x1?: number
  wl?: number
  hh?: number
  dur?: number
  ang?: number
  sp0?: boolean
  sp1?: boolean
  y0?: number
  span?: number
  gifts?: boolean
  gt?: number
  tr?: number
  fp?: number
  fs?: number
  bob?: number
}

type Init = Partial<Particle> & Pick<Particle, 'kind' | 'x' | 'y'>
const P = (o: Init): Particle => ({ vx: 0, vy: 0, rot: 0, t: 0, k: 1, ...o })

// ── Sprite ────────────────────────────────────────────────────────────
function mk(size: number, draw: (g: CanvasRenderingContext2D) => void): HTMLCanvasElement {
  const c = document.createElement('canvas')
  c.width = c.height = size * 2
  const g = c.getContext('2d')!
  g.setTransform(2, 0, 0, 2, size, size)
  draw(g)
  return c
}
/** Trái tim cỡ r (rộng ≈ 1.25r), tâm ở gốc. */
function heartPath(g: CanvasRenderingContext2D, r: number) {
  g.beginPath(); g.moveTo(0, -0.25 * r)
  g.bezierCurveTo(-0.15 * r, -0.55 * r, -0.65 * r, -0.5 * r, -0.62 * r, -0.12 * r)
  g.bezierCurveTo(-0.6 * r, 0.2 * r, -0.25 * r, 0.42 * r, 0, 0.62 * r)
  g.bezierCurveTo(0.25 * r, 0.42 * r, 0.6 * r, 0.2 * r, 0.62 * r, -0.12 * r)
  g.bezierCurveTo(0.65 * r, -0.5 * r, 0.15 * r, -0.55 * r, 0, -0.25 * r)
  g.closePath()
}
function starPath(g: CanvasRenderingContext2D, Ro: number, ri: number, n = 5) {
  g.beginPath()
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 ? ri : Ro
    const a = -Math.PI / 2 + (i * Math.PI) / n
    g.lineTo(Math.cos(a) * r, Math.sin(a) * r)
  }
  g.closePath()
}
const MAPLE: readonly (readonly [number, number])[] = [[0, -10], [2, -5.5], [5, -7.5], [4.2, -2.5], [9, -3.6], [7.2, -0.2], [9.4, 2.2], [4.4, 3], [5.2, 6.2], [1.2, 4.4], [0.6, 9], [-0.6, 9], [-1.2, 4.4], [-5.2, 6.2], [-4.4, 3], [-9.4, 2.2], [-7.2, -0.2], [-9, -3.6], [-4.2, -2.5], [-5, -7.5], [-2, -5.5]]

interface Sprites {
  mai: HTMLCanvasElement; dao: HTMLCanvasElement; coin: HTMLCanvasElement; lixi: HTMLCanvasElement
  maple: HTMLCanvasElement[]; ginkgo: HTMLCanvasElement[]; snow: HTMLCanvasElement; crystal: HTMLCanvasElement
  star: HTMLCanvasElement; gold: HTMLCanvasElement; sparkle: HTMLCanvasElement; sparkleV: HTMLCanvasElement
  bubble: HTMLCanvasElement; drop: HTMLCanvasElement; ghost: HTMLCanvasElement; pumpkin: HTMLCanvasElement
  candy: HTMLCanvasElement[]; gift: HTMLCanvasElement[]
  heart: HTMLCanvasElement[]; petal: HTMLCanvasElement; chung: HTMLCanvasElement; mooncake: HTMLCanvasElement
}
let sprites: Sprites | null = null
/** Vẽ sprite lần đầu cần tới (không chạy lúc nạp module). */
function S(): Sprites {
  if (sprites) return sprites
  sprites = {
    mai: mk(32, (g) => {
      for (let i = 0; i < 5; i++) {
        g.save(); g.rotate((i * TAU) / 5)
        const gr = g.createRadialGradient(0, -3, 1, 0, -7, 8); gr.addColorStop(0, '#FFE680'); gr.addColorStop(1, '#FFC107')
        g.fillStyle = gr; g.beginPath(); g.ellipse(0, -7, 5, 7.2, 0, 0, TAU); g.fill()
        g.strokeStyle = 'rgba(214,130,0,.55)'; g.lineWidth = 0.8; g.stroke(); g.restore()
      }
      g.fillStyle = '#F08C00'; g.beginPath(); g.arc(0, 0, 3, 0, TAU); g.fill()
      g.strokeStyle = '#B85C00'; g.lineWidth = 0.7
      for (let i = 0; i < 8; i++) { const a = (i * TAU) / 8; g.beginPath(); g.moveTo(Math.cos(a) * 2.5, Math.sin(a) * 2.5); g.lineTo(Math.cos(a) * 5.5, Math.sin(a) * 5.5); g.stroke() }
    }),
    dao: mk(28, (g) => {
      const gr = g.createLinearGradient(0, -11, 0, 11); gr.addColorStop(0, '#FFD6E0'); gr.addColorStop(1, '#FF85A6')
      g.fillStyle = gr; g.beginPath(); g.moveTo(0, -7)
      g.bezierCurveTo(3, -12, 10, -9, 9, -1); g.bezierCurveTo(8, 6, 3, 10, 0, 11)
      g.bezierCurveTo(-3, 10, -8, 6, -9, -1); g.bezierCurveTo(-10, -9, -3, -12, 0, -7); g.fill()
      g.strokeStyle = 'rgba(230,73,128,.35)'; g.lineWidth = 0.8; g.beginPath(); g.moveTo(0, -5); g.lineTo(0, 9); g.stroke()
    }),
    coin: mk(30, (g) => {
      const gr = g.createRadialGradient(-3, -3, 1, 0, 0, 12); gr.addColorStop(0, '#FFF0A8'); gr.addColorStop(0.6, '#FFC94A'); gr.addColorStop(1, '#D99A00')
      g.fillStyle = gr; g.beginPath(); g.arc(0, 0, 12, 0, TAU); g.fill()
      g.strokeStyle = '#B7791F'; g.lineWidth = 1.2; g.beginPath(); g.arc(0, 0, 9.5, 0, TAU); g.stroke()
      g.globalCompositeOperation = 'destination-out'; g.fillRect(-3, -3, 6, 6); g.globalCompositeOperation = 'source-over'
      g.strokeRect(-3.5, -3.5, 7, 7)
    }),
    lixi: mk(36, (g) => {
      g.fillStyle = '#D62828'; g.beginPath()
      g.moveTo(-7, -14); g.lineTo(7, -14); g.quadraticCurveTo(10, -14, 10, -11); g.lineTo(10, 11); g.quadraticCurveTo(10, 14, 7, 14)
      g.lineTo(-7, 14); g.quadraticCurveTo(-10, 14, -10, 11); g.lineTo(-10, -11); g.quadraticCurveTo(-10, -14, -7, -14); g.fill()
      g.fillStyle = '#A61B1B'; g.beginPath(); g.moveTo(-10, -12); g.lineTo(0, -3); g.lineTo(10, -12); g.lineTo(10, -14); g.lineTo(-10, -14); g.fill()
      g.fillStyle = '#FFC94A'; g.beginPath(); g.arc(0, -3, 4, 0, TAU); g.fill()
      g.strokeStyle = 'rgba(255,201,74,.85)'; g.lineWidth = 0.9; g.strokeRect(-7, 2, 14, 9)
    }),
    maple: ['#E8590C', '#D9480F', '#C92A2A', '#F08C00', '#E67700'].map((col) => mk(34, (g) => {
      g.fillStyle = col; g.beginPath(); MAPLE.forEach(([x, y]) => g.lineTo(x * 1.45, y * 1.45)); g.closePath(); g.fill()
      g.strokeStyle = 'rgba(255,255,255,.35)'; g.lineWidth = 0.8
      for (const [x, y] of [[0, -13], [13, -5], [-13, -5], [7, 8], [-7, 8]] as const) { g.beginPath(); g.moveTo(0, 5); g.lineTo(x, y); g.stroke() }
      g.strokeStyle = '#7A3E10'; g.lineWidth = 1.2; g.beginPath(); g.moveTo(0, 12); g.lineTo(0, 16); g.stroke()
    })),
    ginkgo: ['#F2C230', '#FAB005'].map((col) => mk(30, (g) => {
      g.fillStyle = col; g.beginPath(); g.moveTo(0, 6); g.lineTo(-11, -4)
      g.quadraticCurveTo(-6, -12, -1, -9); g.lineTo(0, -5); g.lineTo(1, -9); g.quadraticCurveTo(6, -12, 11, -4); g.closePath(); g.fill()
      g.strokeStyle = 'rgba(160,110,0,.4)'; g.lineWidth = 0.6
      for (let i = -3; i <= 3; i++) { g.beginPath(); g.moveTo(0, 6); g.lineTo(i * 3, -8); g.stroke() }
      g.strokeStyle = '#A67C00'; g.lineWidth = 1.2; g.beginPath(); g.moveTo(0, 6); g.lineTo(0, 13); g.stroke()
    })),
    snow: mk(14, (g) => {
      g.shadowColor = 'rgba(60,95,150,.75)'; g.shadowBlur = 3
      const gr = g.createRadialGradient(0, 0, 0, 0, 0, 5); gr.addColorStop(0, '#fff'); gr.addColorStop(0.7, 'rgba(255,255,255,.95)'); gr.addColorStop(1, 'rgba(255,255,255,0)')
      g.fillStyle = gr; g.beginPath(); g.arc(0, 0, 5, 0, TAU); g.fill()
    }),
    crystal: mk(30, (g) => {
      g.shadowColor = 'rgba(50,85,140,.7)'; g.shadowBlur = 3
      g.strokeStyle = '#F4F9FF'; g.lineWidth = 1.8; g.lineCap = 'round'
      for (let i = 0; i < 6; i++) {
        g.save(); g.rotate((i * TAU) / 6); g.beginPath()
        g.moveTo(0, 0); g.lineTo(0, -11); g.moveTo(0, -5); g.lineTo(3, -8); g.moveTo(0, -5); g.lineTo(-3, -8)
        g.moveTo(0, -8.5); g.lineTo(2, -10.5); g.moveTo(0, -8.5); g.lineTo(-2, -10.5); g.stroke(); g.restore()
      }
    }),
    star: mk(24, (g) => { g.shadowColor = 'rgba(230,80,0,.65)'; g.shadowBlur = 4; g.fillStyle = '#FFCD00'; starPath(g, 9, 4); g.fill() }),
    gold: mk(26, (g) => { g.shadowColor = 'rgba(255,170,0,.8)'; g.shadowBlur = 5; g.fillStyle = '#FFD43B'; starPath(g, 10, 4.4); g.fill() }),
    sparkle: mk(22, (g) => { g.shadowColor = 'rgba(255,170,0,.9)'; g.shadowBlur = 5; g.fillStyle = '#FFF3BF'; starPath(g, 8, 2.2, 4); g.fill() }),
    sparkleV: mk(22, (g) => { g.shadowColor = 'rgba(140,82,255,.9)'; g.shadowBlur = 5; g.fillStyle = '#EDE5FF'; starPath(g, 8, 2.2, 4); g.fill() }),
    bubble: mk(32, (g) => {
      const gr = g.createRadialGradient(-4, -4, 1, 0, 0, 13)
      gr.addColorStop(0, 'rgba(255,255,255,.6)'); gr.addColorStop(0.7, 'rgba(160,230,245,.18)'); gr.addColorStop(1, 'rgba(0,150,200,.3)')
      g.fillStyle = gr; g.beginPath(); g.arc(0, 0, 13, 0, TAU); g.fill()
      g.strokeStyle = 'rgba(255,255,255,.9)'; g.lineWidth = 1.2; g.stroke()
      g.strokeStyle = 'rgba(255,255,255,.95)'; g.lineWidth = 2; g.lineCap = 'round'; g.beginPath(); g.arc(0, 0, 9, Math.PI * 1.1, Math.PI * 1.45); g.stroke()
    }),
    drop: mk(22, (g) => {
      const gr = g.createLinearGradient(0, -9, 0, 8); gr.addColorStop(0, '#9BE7FF'); gr.addColorStop(1, '#0096C7')
      g.fillStyle = gr; g.beginPath(); g.moveTo(0, -9); g.bezierCurveTo(4, -3, 7, 1, 7, 4); g.arc(0, 4, 7, 0, Math.PI); g.bezierCurveTo(-7, 1, -4, -3, 0, -9); g.fill()
    }),
    ghost: mk(34, (g) => {
      g.shadowColor = 'rgba(110,70,190,.45)'; g.shadowBlur = 4; g.fillStyle = 'rgba(255,255,255,.96)'; g.beginPath()
      g.moveTo(-10, 9); g.lineTo(-10, -3); g.arc(0, -3, 10, Math.PI, 0); g.lineTo(10, 9)
      g.quadraticCurveTo(7.5, 13, 5, 9); g.quadraticCurveTo(2.5, 13, 0, 9); g.quadraticCurveTo(-2.5, 13, -5, 9); g.quadraticCurveTo(-7.5, 13, -10, 9); g.fill()
      g.shadowBlur = 0; g.fillStyle = '#2A1638'
      g.beginPath(); g.ellipse(-3.5, -4, 1.6, 2.3, 0, 0, TAU); g.fill(); g.beginPath(); g.ellipse(3.5, -4, 1.6, 2.3, 0, 0, TAU); g.fill()
      g.beginPath(); g.ellipse(0, 1.5, 1.6, 1.9, 0, 0, TAU); g.fill()
    }),
    pumpkin: mk(30, (g) => {
      g.fillStyle = '#E8590C'; g.beginPath(); g.ellipse(-5, 1, 6.5, 8, 0, 0, TAU); g.fill(); g.beginPath(); g.ellipse(5, 1, 6.5, 8, 0, 0, TAU); g.fill()
      g.fillStyle = '#FD7E14'; g.beginPath(); g.ellipse(0, 1, 6.5, 8.6, 0, 0, TAU); g.fill()
      g.strokeStyle = '#3F7D20'; g.lineWidth = 2; g.beginPath(); g.moveTo(0, -7); g.quadraticCurveTo(2, -12, 5, -11); g.stroke()
      g.fillStyle = '#FFD43B'; g.beginPath(); g.moveTo(-5, -1); g.lineTo(-2, -1); g.lineTo(-3.5, -4); g.closePath(); g.moveTo(2, -1); g.lineTo(5, -1); g.lineTo(3.5, -4); g.closePath(); g.fill()
      g.beginPath(); g.moveTo(-5, 3); g.quadraticCurveTo(0, 8, 5, 3); g.lineTo(3, 3.8); g.lineTo(1.5, 2.6); g.lineTo(0, 4); g.lineTo(-1.5, 2.6); g.lineTo(-3, 3.8); g.closePath(); g.fill()
    }),
    candy: [['#FF922B', '#FFF'], ['#9775FA', '#FFD43B'], ['#51CF66', '#FFF']].map(([c1, c2]) => mk(30, (g) => {
      g.fillStyle = c1!; g.beginPath(); g.moveTo(-6, 0); g.lineTo(-13, -5); g.lineTo(-12, 0); g.lineTo(-13, 5); g.closePath(); g.fill()
      g.beginPath(); g.moveTo(6, 0); g.lineTo(13, -5); g.lineTo(12, 0); g.lineTo(13, 5); g.closePath(); g.fill()
      g.beginPath(); g.arc(0, 0, 7, 0, TAU); g.fill()
      g.strokeStyle = c2!; g.lineWidth = 1.6; g.beginPath(); g.arc(0, 0, 4, 0, Math.PI * 1.4); g.stroke()
    })),
    gift: [['#E03131', '#FFD43B'], ['#2B8A3E', '#FFFFFF'], ['#4DABF7', '#E03131'], ['#FFD43B', '#E03131']].map(([c1, c2]) => mk(28, (g) => {
      g.fillStyle = c1!; g.fillRect(-8, -5, 16, 13); g.fillRect(-9.5, -8, 19, 4)
      g.fillStyle = c2!; g.fillRect(-1.5, -8, 3, 16); g.fillRect(-8, 0, 16, 2)
      g.strokeStyle = c2!; g.lineWidth = 1.8; g.beginPath(); g.ellipse(-3.5, -10, 3.2, 2, -0.5, 0, TAU); g.stroke(); g.beginPath(); g.ellipse(3.5, -10, 3.2, 2, 0.5, 0, TAU); g.stroke()
    })),
    heart: [['#FF6B8B', '#D6336C'], ['#FFB3C8', '#F06595'], ['#FF8787', '#E03131']].map(([c1, c2]) => mk(28, (g) => {
      g.shadowColor = 'rgba(214,51,108,.45)'; g.shadowBlur = 3
      const gr = g.createLinearGradient(-6, -8, 6, 9); gr.addColorStop(0, c1!); gr.addColorStop(1, c2!)
      g.fillStyle = gr; heartPath(g, 19); g.fill(); g.shadowBlur = 0
      g.fillStyle = 'rgba(255,255,255,.6)'; g.beginPath(); g.ellipse(-5.6, -4.6, 2.6, 1.5, -0.7, 0, TAU); g.fill()
    })),
    petal: mk(26, (g) => {
      const gr = g.createLinearGradient(0, -10, 0, 10); gr.addColorStop(0, '#FF4D6D'); gr.addColorStop(1, '#A4133C')
      g.fillStyle = gr; g.beginPath(); g.moveTo(0, 10)
      g.bezierCurveTo(-9, 6, -10, -4, -5, -9); g.quadraticCurveTo(0, -6, 5, -9); g.bezierCurveTo(10, -4, 9, 6, 0, 10); g.fill()
      g.strokeStyle = 'rgba(255,255,255,.25)'; g.lineWidth = 0.8; g.beginPath(); g.moveTo(0, 8); g.quadraticCurveTo(-1, 0, 0, -6); g.stroke()
    }),
    // Bánh trung thu nhìn từ trên: viền lượn sóng, hoa văn giữa
    mooncake: mk(30, (g) => {
      g.fillStyle = '#D08A3A'
      for (let i = 0; i < 14; i++) { const a = (i * TAU) / 14; g.beginPath(); g.arc(Math.cos(a) * 10.2, Math.sin(a) * 10.2, 2.6, 0, TAU); g.fill() }
      const gr = g.createRadialGradient(-3, -3, 1, 0, 0, 11); gr.addColorStop(0, '#F4C47A'); gr.addColorStop(1, '#C97F2E')
      g.fillStyle = gr; g.beginPath(); g.arc(0, 0, 10.4, 0, TAU); g.fill()
      g.strokeStyle = '#A8611E'; g.lineWidth = 0.9; g.beginPath(); g.arc(0, 0, 6.6, 0, TAU); g.stroke()
      g.fillStyle = '#A8611E'
      for (let i = 0; i < 4; i++) { const a = (i * TAU) / 4; g.beginPath(); g.ellipse(Math.cos(a) * 3, Math.sin(a) * 3, 1.6, 1, a, 0, TAU); g.fill() }
      g.beginPath(); g.arc(0, 0, 1.2, 0, TAU); g.fill()
    }),
    // Bánh chưng nhìn chéo: mặt trên + mặt trước, buộc lạt
    chung: mk(32, (g) => {
      g.fillStyle = '#2F6B1A'; g.beginPath(); g.moveTo(-11, -3); g.lineTo(11, -3); g.lineTo(11, 12); g.lineTo(-11, 12); g.closePath(); g.fill()
      g.fillStyle = '#5E9E35'; g.beginPath(); g.moveTo(-11, -3); g.lineTo(-5, -11); g.lineTo(13, -11); g.lineTo(11, -3); g.closePath(); g.fill()
      g.strokeStyle = 'rgba(20,60,10,.35)'; g.lineWidth = 0.6
      for (const y of [1, 5, 9]) { g.beginPath(); g.moveTo(-10, y); g.lineTo(10, y + 0.6); g.stroke() }
      g.strokeStyle = '#F1E2B0'; g.lineWidth = 1.4
      g.beginPath(); g.moveTo(-4, 12); g.lineTo(-4, -3); g.lineTo(1, -11); g.moveTo(4, 12); g.lineTo(4, -3); g.lineTo(8, -11)
      g.moveTo(-9, -5.5); g.lineTo(11.6, -5.5); g.moveTo(-6.6, -8.7); g.lineTo(12.4, -8.7); g.stroke()
    }),
  }
  return sprites
}

// ── Bộ máy ────────────────────────────────────────────────────────────
const FW_COLS = ['#E03131', '#F03E3E', '#FAB005', '#FFC400', '#FF6B00'] as const

/** Mặt biển của sân khấu "sea": 3 lớp sóng (sau, giữa, trước) vẽ thẳng lên canvas. */
interface WaveLayer { base: number; A: number; len: number; speed: number; ph: number; top: string; bottom: string; foam?: boolean }

export class Engine {
  readonly cv: HTMLCanvasElement
  readonly g: CanvasRenderingContext2D
  p: Particle[] = []
  w = 1
  h = 1
  d = 1
  key: SeasonKey | null = null
  target = 0
  fwEvery = 0
  fw = 0
  tick: ((e: Engine, dt: number) => void) | null = null
  timer = 0
  timer2 = 0
  clock = R(0, 100)
  /** Sân khấu: mực nước (px tính từ đáy), biên độ sóng, tỉ lệ nhân vật. */
  wl = 0
  amp = 0
  sc = 1
  waves: WaveLayer[] = []
  surfer = false
  surferOn = false
  dolphins = false

  constructor(cv: HTMLCanvasElement) {
    this.cv = cv
    this.g = cv.getContext('2d')!
  }

  resize(w: number, h: number) {
    this.d = Math.min(window.devicePixelRatio || 1, 2)
    this.w = w || 1
    this.h = h || 1
    this.cv.width = Math.round(this.w * this.d)
    this.cv.height = Math.round(this.h * this.d)
  }

  setAmbient(key: SeasonKey | null, count: number, fwEvery: number, instant: boolean) {
    if (instant) this.p = []
    else for (const q of this.p) q.fade = true
    this.key = key
    this.target = count
    this.fwEvery = fwEvery
    this.fw = 0.5
    const f = key ? AMB[key] : undefined
    if (f) for (let i = 0; i < count; i++) { const q = f(this, true); q.k = instant ? 1 : 0; this.p.push(q) }
  }

  step(dt: number) {
    this.clock += dt
    if (this.tick) this.tick(this, dt)
    if (this.fwEvery) {
      this.fw -= dt
      if (this.fw <= 0) { this.fw = this.fwEvery * R(0.7, 1.3); this.p.push(rocket(R(0.15, 0.85) * this.w, this.h * R(0.5, 0.68), this.h * R(0.1, 0.3), 0.75)) }
    }
    let live = 0
    for (let i = 0; i < this.p.length; i++) {
      const q = this.p[i]!
      if ((q.delay ?? 0) > 0) { q.delay! -= dt; continue }
      update(this, q, dt)
      if (q.amb && !q.fade && !q.dead) live++
    }
    const f = this.key ? AMB[this.key] : undefined
    if (f) for (let i = live; i < this.target; i++) this.p.push(f(this, false))
    this.p = this.p.filter((q) => !q.dead)
  }

  /** Độ cao mặt sóng lớp i tại x (toạ độ canvas). */
  waveY(i: number, x: number): number {
    const L = this.waves[i]
    if (!L) return this.h - this.wl
    const t = this.clock
    return this.h - L.base
      + L.A * Math.sin((x / L.len) * TAU - t * L.speed + L.ph)
      + L.A * 0.35 * Math.sin((x / (L.len * 0.47)) * TAU + t * L.speed * 1.3 + L.ph * 2)
      + L.A * 0.3 * Math.sin(t * 0.45 + L.ph)
  }

  private drawWave(i: number) {
    const L = this.waves[i]
    if (!L) return
    const g = this.g
    let top = this.h
    g.beginPath(); g.moveTo(0, this.h)
    for (let x = 0; x <= this.w + 6; x += 6) { const y = this.waveY(i, x); if (y < top) top = y; g.lineTo(x, y) }
    g.lineTo(this.w, this.h); g.closePath()
    const gr = g.createLinearGradient(0, top, 0, this.h); gr.addColorStop(0, L.top); gr.addColorStop(1, L.bottom)
    g.fillStyle = gr; g.fill()
    if (L.foam) {
      g.beginPath()
      for (let x = 0; x <= this.w + 6; x += 6) g.lineTo(x, this.waveY(i, x) + 1)
      g.strokeStyle = 'rgba(255,255,255,.85)'; g.lineWidth = 2.2; g.stroke()
      // bọt trắng lốm đốm ở đỉnh sóng
      g.fillStyle = 'rgba(255,255,255,.7)'
      for (let x = 8; x < this.w; x += 22) {
        const y = this.waveY(i, x), y2 = this.waveY(i, x + 3)
        if (y2 < y - 0.4) { g.beginPath(); g.arc(x, y + 3, 1.6 + ((x * 7) % 3) * 0.5, 0, TAU); g.fill() }
      }
    }
  }

  draw() {
    const g = this.g, d = this.d
    g.setTransform(1, 0, 0, 1, 0, 0)
    g.clearRect(0, 0, this.cv.width, this.cv.height)
    if (this.waves.length) { g.setTransform(d, 0, 0, d, 0, 0); g.globalAlpha = 1; this.drawWave(0); this.drawWave(1) }
    for (const q of this.p) {
      if ((q.delay ?? 0) > 0) continue
      let a = (q.A ?? 1) * q.k
      if (q.ml && q.life != null) a *= Math.min(1, q.life / (q.ml * 0.4))
      if (q.tw) a *= 0.3 + 0.7 * (0.5 + 0.5 * Math.sin(q.t * q.tw + (q.ph ?? 0)))
      if (a <= 0.01) continue
      g.globalAlpha = Math.min(1, a)
      if (q.drw) {
        g.setTransform(d, 0, 0, d, q.x * d, q.y * d)
        q.drw(g, q, this)
      } else if (q.kind === 'spr' || q.kind === 'rect') {
        const fx = q.fl ? Math.cos(q.t * q.fl + (q.ph ?? 0)) : 1, c = Math.cos(q.rot), s = Math.sin(q.rot)
        g.setTransform(d * c * fx, d * s * fx, -d * s, d * c, q.x * d, q.y * d)
        const sz = q.s ?? 10
        if (q.kind === 'spr' && q.spr) g.drawImage(q.spr, -sz / 2, -sz / 2, sz, sz)
        else { g.fillStyle = q.c ?? '#fff'; g.fillRect(-sz / 2, -sz * 0.3, sz, sz * 0.6) }
      } else if (q.kind === 'spark') {
        g.setTransform(d, 0, 0, d, 0, 0); g.strokeStyle = q.c ?? '#FFC400'; g.lineWidth = q.sz ?? 1.6; g.lineCap = 'round'
        const h = q.hist ?? []
        g.beginPath(); g.moveTo(h[0] ?? q.x, h[1] ?? q.y); g.lineTo(q.x, q.y); g.stroke()
      } else if (q.kind === 'rocket') {
        g.setTransform(d, 0, 0, d, 0, 0)
        const gr = g.createLinearGradient(q.x, q.y, q.x, q.y + 26); gr.addColorStop(0, 'rgba(255,190,40,1)'); gr.addColorStop(1, 'rgba(255,190,40,0)')
        g.strokeStyle = gr; g.lineWidth = 2.2; g.lineCap = 'round'; g.beginPath(); g.moveTo(q.x, q.y); g.lineTo(q.x - q.vx * 0.07, q.y + 26); g.stroke()
      }
    }
    g.globalAlpha = 1
    if (this.waves.length > 2) { g.setTransform(d, 0, 0, d, 0, 0); this.drawWave(2) }
  }
}

function update(e: Engine, q: Particle, dt: number) {
  q.t += dt
  if (q.kind === 'rocket') {
    q.x += q.vx * dt; q.y += q.vy * dt
    if (q.y <= (q.ty ?? 0)) { q.dead = true; explode(e, q.x, q.y, q.cols ?? FW_COLS, q.n ?? 46, q.pow ?? 1, q.A ?? 1) }
    return
  }
  if (q.upd) q.upd(e, q, dt)
  else {
    if (q.g) q.vy += q.g * dt
    if (q.drag) { const dr = Math.pow(q.drag, dt * 60); q.vx *= dr; q.vy *= dr }
    if (q.hist) { q.hist.push(q.x, q.y); if (q.hist.length > 10) q.hist.splice(0, 2) }
    q.x += (q.vx + (q.sa ? Math.cos(q.t * (q.sf ?? 1) + (q.ph ?? 0)) * q.sa : 0)) * dt
    q.y += q.vy * dt
    q.rot += (q.vr ?? 0) * dt
  }
  if (q.life != null) { q.life -= dt; if (q.life <= 0) q.dead = true }
  if (q.fade) { q.k -= dt * 1.8; if (q.k <= 0) q.dead = true } else if (q.k < 1) q.k = Math.min(1, q.k + dt * 1.5)
  if (q.upd) return
  if (q.amb) { if (((q.dir ?? 1) > 0 && q.y > e.h + 40) || ((q.dir ?? 1) < 0 && q.y < -40) || q.x < -80 || q.x > e.w + 80) q.dead = true }
  else if (q.y > e.h + 80) q.dead = true
}

function explode(e: Engine, x: number, y: number, cols: readonly string[], n: number, pow: number, A: number) {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + R(-0.08, 0.08), s = R(0.45, 1) * pow * 160
    e.p.push(P({ kind: 'spark', x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, g: 70, drag: 0.972, life: R(1, 1.6), ml: 1.6, c: pick(cols), hist: [], A, sz: R(1.2, 2.1) }))
  }
}
function rocket(x: number, y: number, ty: number, A: number, n = 46, pow = 1): Particle {
  return P({ kind: 'rocket', x, y, vx: R(-15, 15), vy: -R(300, 380), ty, cols: FW_COLS, n, pow, A })
}

// ── Nhân vật ──────────────────────────────────────────────────────────
function drawDolphin(g: CanvasRenderingContext2D, s: number) {
  g.scale(s, s)
  g.fillStyle = '#245A86'
  g.beginPath(); g.moveTo(-2, -9.5); g.quadraticCurveTo(-6, -19, -13, -21); g.quadraticCurveTo(-10, -14, -12, -8.5); g.closePath(); g.fill()
  g.beginPath(); g.moveTo(-28, -1); g.quadraticCurveTo(-35, -9, -42, -11); g.quadraticCurveTo(-38, -4, -37, -1); g.quadraticCurveTo(-38, 3, -42, 8); g.quadraticCurveTo(-35, 7, -28, 1); g.closePath(); g.fill()
  const gr = g.createLinearGradient(0, -12, 0, 8); gr.addColorStop(0, '#2F6FA3'); gr.addColorStop(0.55, '#5FA8D8'); gr.addColorStop(1, '#DDF3FF')
  g.fillStyle = gr; g.beginPath(); g.moveTo(32, 1)
  g.bezierCurveTo(28, -1, 24, -3, 20, -4); g.bezierCurveTo(16, -11, 4, -12, -6, -10); g.bezierCurveTo(-16, -8, -24, -4, -30, -1)
  g.bezierCurveTo(-24, 3, -14, 7, -2, 7); g.bezierCurveTo(10, 7, 22, 5, 32, 1); g.closePath(); g.fill()
  g.fillStyle = '#2B6597'; g.beginPath(); g.moveTo(10, 4); g.quadraticCurveTo(6, 12, -1, 14); g.quadraticCurveTo(3, 8, 3, 5); g.closePath(); g.fill()
  g.fillStyle = 'rgba(255,255,255,.5)'; g.beginPath(); g.ellipse(6, -8, 8, 1.5, -0.06, 0, TAU); g.fill()
  g.fillStyle = '#0B2A40'; g.beginPath(); g.arc(19, -3, 1.3, 0, TAU); g.fill()
  g.strokeStyle = 'rgba(10,40,60,.45)'; g.lineWidth = 0.8; g.beginPath(); g.moveTo(31, 1.6); g.quadraticCurveTo(26, 3, 22, 2.2); g.stroke()
}

/** Người lướt sóng (toạ độ: tâm ván ở gốc, quay mặt sang phải). */
function drawSurfer(g: CanvasRenderingContext2D, s: number, t: number) {
  g.scale(s, s)
  const k = 0.5 + 0.5 * Math.sin(t * 2.4)   // nhún gối
  // vây ván + ván
  g.fillStyle = '#1E2732'; g.beginPath(); g.moveTo(-14, 2); g.lineTo(-17, 6); g.lineTo(-11, 2); g.closePath(); g.fill()
  const gr = g.createLinearGradient(-20, 0, 22, 0); gr.addColorStop(0, '#FF6B6B'); gr.addColorStop(1, '#FFA94D')
  g.fillStyle = gr; g.beginPath()
  g.moveTo(-20, 0); g.quadraticCurveTo(-19, -3, -6, -3); g.lineTo(14, -2.6); g.quadraticCurveTo(23, -2, 22, 0)
  g.quadraticCurveTo(21, 2, 10, 2.2); g.lineTo(-14, 2.2); g.quadraticCurveTo(-20, 2.2, -20, 0); g.closePath(); g.fill()
  g.strokeStyle = '#FFE066'; g.lineWidth = 0.9; g.beginPath(); g.moveTo(-17, -0.4); g.lineTo(19, -0.4); g.stroke()
  // chân (quần xanh đậm), gối khuỵu nhịp nhàng
  const hipY = -13.5 + k * 2
  g.strokeStyle = '#1E3A5F'; g.lineWidth = 2.6; g.lineCap = 'round'; g.lineJoin = 'round'
  g.beginPath(); g.moveTo(-7, -2.6); g.lineTo(-4.2, -8.4 + k); g.lineTo(-1.2, hipY); g.stroke()
  g.beginPath(); g.moveTo(6, -2.6); g.lineTo(5.4, -8.4 + k); g.lineTo(1.4, hipY); g.stroke()
  // thân (áo bơi cam), tay dang giữ thăng bằng
  const shY = -22 + k * 1.5
  g.strokeStyle = '#F76707'; g.lineWidth = 3.6; g.beginPath(); g.moveTo(0, hipY); g.lineTo(1.6, shY); g.stroke()
  g.strokeStyle = '#E8B48A'; g.lineWidth = 1.8
  g.beginPath(); g.moveTo(1.2, shY + 1); g.lineTo(-4, shY + 3 + Math.sin(t * 3) * 1.5); g.lineTo(-9, shY + 1 + Math.sin(t * 3) * 2); g.stroke()
  g.beginPath(); g.moveTo(1.8, shY + 1); g.lineTo(6.5, shY + 3.5); g.lineTo(11, shY + 5 - Math.sin(t * 3) * 1.5); g.stroke()
  // đầu + tóc
  g.fillStyle = '#E8B48A'; g.beginPath(); g.arc(2.6, shY - 3.6, 3, 0, TAU); g.fill()
  g.fillStyle = '#3B2A1A'; g.beginPath(); g.arc(2.2, shY - 4.4, 3, Math.PI * 1.05, Math.PI * 2.05); g.fill()
}

function drawReindeer(g: CanvasRenderingContext2D, x: number, t: number, ph: number, rudolph: boolean) {
  g.save(); g.translate(x, 0)
  const sw = Math.sin(t * 11 + ph) * 0.7
  g.strokeStyle = '#6B4423'; g.lineWidth = 1.6; g.lineCap = 'round'
  for (const [lx, a] of [[5, sw], [3, sw * 0.8], [-5, -sw], [-7, -sw * 0.8]] as const) { g.beginPath(); g.moveTo(lx, 3); g.lineTo(lx + Math.sin(a) * 7, 3 + Math.cos(a) * 7); g.stroke() }
  g.fillStyle = '#9C6A3C'; g.beginPath(); g.ellipse(0, 0, 9, 4.6, 0, 0, TAU); g.fill()
  g.fillStyle = '#F1E3CF'; g.beginPath(); g.ellipse(-8.5, -1.5, 2, 1.6, 0, 0, TAU); g.fill()
  g.fillStyle = '#9C6A3C'; g.beginPath(); g.moveTo(5, -2); g.lineTo(9, -9); g.lineTo(12, -8); g.lineTo(9, 0); g.closePath(); g.fill()
  g.beginPath(); g.ellipse(12, -9.5, 3.6, 2.6, 0.2, 0, TAU); g.fill()
  g.strokeStyle = '#5A3A1C'; g.lineWidth = 1.1
  g.beginPath(); g.moveTo(11, -12); g.lineTo(9, -18); g.moveTo(10, -15.5); g.lineTo(7, -17); g.moveTo(9.6, -17); g.lineTo(11, -20); g.moveTo(13, -12); g.lineTo(14.5, -17.5); g.moveTo(14, -15.5); g.lineTo(17, -17); g.stroke()
  g.fillStyle = '#1E1E1E'; g.beginPath(); g.arc(12.6, -10.4, 0.7, 0, TAU); g.fill()
  if (rudolph) { g.shadowColor = 'rgba(255,40,40,.95)'; g.shadowBlur = 8; g.fillStyle = '#FF2D2D' } else g.fillStyle = '#3B2614'
  g.beginPath(); g.arc(15.6, -9, rudolph ? 1.7 : 1.1, 0, TAU); g.fill(); g.shadowBlur = 0
  g.restore()
}
function drawSleigh(g: CanvasRenderingContext2D) {
  g.fillStyle = '#8B5A2B'; g.beginPath(); g.arc(-11, -9, 6.5, 0, TAU); g.fill()
  g.fillStyle = '#2B8A3E'; g.fillRect(-16, -16, 5, 5); g.fillStyle = '#4DABF7'; g.fillRect(-10, -18, 5, 6); g.fillStyle = '#FFD43B'; g.fillRect(-10, -15.5, 5, 1)
  g.fillStyle = '#D62828'; g.beginPath(); g.arc(0, -9, 6, 0, TAU); g.fill()
  g.fillStyle = '#1E1E1E'; g.fillRect(-5.5, -7.2, 11, 1.6)
  g.fillStyle = '#F8D3B5'; g.beginPath(); g.arc(1.5, -17, 3.4, 0, TAU); g.fill()
  g.fillStyle = '#fff'; g.beginPath(); g.arc(2.2, -14.2, 3.4, 0, TAU); g.fill()
  g.fillStyle = '#D62828'; g.beginPath(); g.moveTo(-2, -19); g.quadraticCurveTo(2, -25, 7, -23); g.lineTo(4.5, -19); g.closePath(); g.fill()
  g.fillStyle = '#fff'; g.fillRect(-2.2, -20, 7.4, 1.8); g.beginPath(); g.arc(7.2, -23, 1.5, 0, TAU); g.fill()
  g.strokeStyle = '#D62828'; g.lineWidth = 2.4; g.lineCap = 'round'; g.beginPath(); g.moveTo(3, -10); g.lineTo(8, -8); g.stroke()
  g.fillStyle = '#C92A2A'; g.beginPath(); g.moveTo(-17, -6); g.quadraticCurveTo(-20, -6, -19, -1); g.lineTo(-16, 5); g.lineTo(12, 5); g.quadraticCurveTo(16, 5, 15, -1); g.lineTo(9, -1); g.lineTo(7, -4); g.lineTo(-14, -4); g.closePath(); g.fill()
  g.fillStyle = '#FFD43B'; g.fillRect(-14, 0, 26, 1.4)
  g.strokeStyle = '#E0A100'; g.lineWidth = 1.4; g.beginPath(); g.moveTo(-18, 9); g.lineTo(14, 9); g.quadraticCurveTo(20, 9, 19, 4); g.moveTo(-10, 5); g.lineTo(-10, 9); g.moveTo(6, 5); g.lineTo(6, 9); g.stroke()
}
function drawSleighTeam(g: CanvasRenderingContext2D, s: number, t: number) {
  g.scale(s, s)
  g.strokeStyle = 'rgba(122,74,28,.8)'; g.lineWidth = 0.9; g.beginPath(); g.moveTo(8, -8); g.quadraticCurveTo(30, -2, 52, -6); g.lineTo(100, -6); g.stroke()
  drawReindeer(g, 94, t, 0, true); drawReindeer(g, 70, t, 1.4, false); drawReindeer(g, 46, t, 2.6, false)
  drawSleigh(g)
}
function drawBat(g: CanvasRenderingContext2D, s: number, flap: number) {
  g.scale(s, s); const f = Math.sin(flap)
  g.fillStyle = '#2A1638'
  for (const m of [1, -1]) {
    g.save(); g.scale(m, 1); g.beginPath(); g.moveTo(2, -1)
    g.quadraticCurveTo(7, -7 - 6 * f, 14, -4 - 8 * f); g.quadraticCurveTo(12.5, -1 - 4 * f, 13, 2 - 5 * f)
    g.quadraticCurveTo(10, -2 * f, 9, 3 - 2 * f); g.quadraticCurveTo(6, 1, 2, 3); g.closePath(); g.fill(); g.restore()
  }
  g.beginPath(); g.ellipse(0, 1, 3.2, 4.2, 0, 0, TAU); g.fill()
  g.beginPath(); g.arc(0, -3, 2.6, 0, TAU); g.fill()
  g.beginPath(); g.moveTo(-2.2, -4); g.lineTo(-1.4, -7.5); g.lineTo(-0.4, -4.6); g.closePath(); g.moveTo(2.2, -4); g.lineTo(1.4, -7.5); g.lineTo(0.4, -4.6); g.closePath(); g.fill()
  g.fillStyle = '#FFB000'; g.beginPath(); g.arc(-1, -3.2, 0.6, 0, TAU); g.fill(); g.beginPath(); g.arc(1, -3.2, 0.6, 0, TAU); g.fill()
}
function drawWitch(g: CanvasRenderingContext2D, s: number, t: number) {
  g.scale(s, s); g.fillStyle = '#1E1030'; g.strokeStyle = '#1E1030'
  g.lineWidth = 1.8; g.lineCap = 'round'; g.beginPath(); g.moveTo(-24, 5); g.lineTo(18, 0); g.stroke()
  g.beginPath(); g.moveTo(-22, 4.5); g.lineTo(-34, -2); g.lineTo(-33, 6); g.lineTo(-35, 12); g.closePath(); g.fill()
  const c = Math.sin(t * 9) * 2
  g.beginPath(); g.moveTo(-2, -10); g.quadraticCurveTo(-12, -6 + c, -18, -2 + c); g.lineTo(-6, 2); g.closePath(); g.fill()
  g.beginPath(); g.moveTo(-6, 3); g.lineTo(0, -11); g.lineTo(5, 3); g.closePath(); g.fill()
  g.beginPath(); g.arc(1, -13, 2.8, 0, TAU); g.fill()
  g.beginPath(); g.moveTo(-5, -15); g.lineTo(7, -15.5); g.lineTo(2.5, -17); g.quadraticCurveTo(1, -24, -3, -27); g.quadraticCurveTo(-1, -21, -1.5, -16.6); g.closePath(); g.fill()
  g.lineWidth = 1.4; g.beginPath(); g.moveTo(1, -7); g.lineTo(8, 0.5); g.stroke()
}

function splash(e: Engine, x: number, y: number, s: number, n = 12) {
  for (let i = 0; i < n; i++) e.p.push(P({ kind: 'spr', spr: S().drop, s: R(5, 9) * s, x: x + R(-6, 6), y, vx: R(-70, 70) * s, vy: -R(90, 190) * s, g: 560, rot: R(-0.4, 0.4), life: R(0.5, 0.9), ml: 0.9 }))
}

// Cá heo nhảy theo cung parabol; phần dưới mặt nước vẽ mờ như nhìn xuyên nước
function dolphinActor(o: { x0: number; x1: number; wl: number; hh: number; dur: number; s: number; delay?: number }): Particle {
  return P({ kind: 'act', upd: dolphinUpd, drw: dolphinDrw, x: o.x0, y: o.wl, x0: o.x0, x1: o.x1, wl: o.wl, hh: o.hh, dur: o.dur, s: o.s, delay: o.delay ?? 0, ang: 0 })
}
function dolphinUpd(e: Engine, q: Particle) {
  const u = q.t / q.dur!
  if (u >= 1) { q.dead = true; return }
  const dx = q.x1! - q.x0!
  q.x = q.x0! + dx * u
  q.y = q.wl! - Math.sin(Math.PI * u) * q.hh!
  q.ang = Math.atan2(-Math.PI * q.hh! * Math.cos(Math.PI * u), dx)
  if (!q.sp0 && u > 0.05) { q.sp0 = true; splash(e, q.x0! + dx * 0.03, q.wl!, q.s!) }
  if (!q.sp1 && u > 0.93) { q.sp1 = true; splash(e, q.x0! + dx * 0.96, q.wl!, q.s!) }
}
function dolphinDrw(g: CanvasRenderingContext2D, q: Particle) {
  const under = q.wl! - q.y
  const body = (alpha: number, y0: number, hgt: number) => {
    g.save(); g.globalAlpha *= alpha; g.beginPath(); g.rect(-160, y0, 320, hgt); g.clip()
    g.rotate(q.ang ?? 0); if (q.x1! < q.x0!) g.scale(1, -1); drawDolphin(g, q.s!); g.restore()
  }
  body(1, -400, under + 401)
  body(0.25, under + 1, 400)
}
function jumpDolphins(e: Engine, n: number, wl = e.h - e.wl) {
  const L = Math.min(e.w * 0.7, R(130, 200) * e.sc), dir = Math.random() < 0.5 ? 1 : -1
  const x0 = dir > 0 ? R(e.w * 0.04, Math.max(e.w * 0.05, e.w * 0.96 - L)) : R(Math.min(e.w * 0.95, L + e.w * 0.04), e.w * 0.96)
  const hh = Math.max(20, Math.min(wl - 12, R(0.55, 0.85) * wl)), dur = R(1.4, 1.9), s = e.sc * R(0.9, 1.1)
  for (let i = 0; i < n; i++) {
    const off = -dir * i * 26 * e.sc
    e.p.push(dolphinActor({ x0: x0 + off, x1: x0 + off + dir * L, wl, hh: hh * (1 - i * 0.12), dur, s: s * (1 - i * 0.1), delay: i * 0.3 }))
  }
}

// Người lướt sóng bám đỉnh lớp sóng giữa, lượn tới lui, tung bọt nước ở đuôi ván
function surferActor(e: Engine): Particle {
  const dir = Math.random() < 0.7 ? 1 : -1
  return P({ kind: 'act', upd: surferUpd, drw: surferDrw, x: dir > 0 ? -60 : e.w + 60, y: e.h, dir, dur: R(9, 12) * Math.max(1, e.w / 520), s: e.sc * 1.1, tr: 0, ang: 0 })
}
function surferUpd(e: Engine, q: Particle, dt: number) {
  const u = q.t / q.dur!, dir = q.dir!, s = q.s!
  if (u >= 1) { q.dead = true; e.surferOn = false; e.timer = R(1.2, 3); return }
  q.x = (dir > 0 ? -50 * s : e.w + 50 * s) + dir * (e.w + 100 * s) * u + Math.sin(q.t * 1.1) * 16 * s
  const y = e.waveY(1, q.x), yl = e.waveY(1, q.x - 5), yr = e.waveY(1, q.x + 5)
  q.y = y - 1.5 * s
  q.ang = Math.atan2(yr - yl, 10) * 0.9
  q.tr = (q.tr ?? 0) - dt
  if (q.tr <= 0) {
    q.tr = 0.05
    const tx = q.x - dir * 18 * s
    e.p.push(P({ kind: 'spr', spr: S().drop, s: R(3, 6) * s, x: tx, y: e.waveY(1, tx), vx: -dir * R(20, 70), vy: -R(50, 120) * s, g: 420, rot: R(-0.5, 0.5), life: R(0.4, 0.7), ml: 0.7 }))
  }
}
function surferDrw(g: CanvasRenderingContext2D, q: Particle) {
  g.rotate(q.ang ?? 0)
  if ((q.dir ?? 1) < 0) g.scale(-1, 1)
  drawSurfer(g, q.s!, q.t)
}

function sleighActor(e: Engine, o: { y0: number; dur: number; s: number; gifts?: boolean; dir?: number }): Particle {
  const dir = o.dir ?? (Math.random() < 0.5 ? 1 : -1)
  return P({ kind: 'act', upd: sleighUpd, drw: sleighDrw, x: -999, y: 0, dir, y0: o.y0, dur: o.dur, s: o.s, span: e.w + 280 * o.s, gifts: !!o.gifts, gt: 0.2, tr: 0 })
}
function sleighUpd(e: Engine, q: Particle, dt: number) {
  const u = q.t / q.dur!, dir = q.dir!, s = q.s!
  if (u >= 1) { q.dead = true; return }
  q.x = (dir > 0 ? -150 * s : e.w + 150 * s) + dir * q.span! * u
  q.y = q.y0! + Math.sin(u * TAU) * 8 * s + Math.sin(q.t * 3) * 2
  q.tr = (q.tr ?? 0) - dt
  if (q.tr <= 0) {
    q.tr = 0.035
    e.p.push(P({ kind: 'spr', spr: pick([S().gold, S().sparkle]), s: R(5, 9) * s, x: q.x - dir * 22 * s, y: q.y + R(-2, 6) * s, vx: -dir * R(5, 20), vy: R(-6, 14), rot: R(0, TAU), vr: R(-2, 2), life: R(0.6, 1), ml: 1 }))
  }
  if (q.gifts) {
    q.gt = (q.gt ?? 0) - dt
    if (q.gt <= 0 && q.x > 20 && q.x < e.w - 20) {
      q.gt = R(0.22, 0.38)
      e.p.push(P({ kind: 'spr', spr: pick(S().gift), s: R(16, 22) * s, x: q.x - dir * 6 * s, y: q.y, vx: dir * R(10, 50), vy: R(-30, 10), g: 520, rot: R(0, TAU), vr: R(-3, 3), life: R(1.6, 2.2), ml: 2.2 }))
    }
  }
}
function sleighDrw(g: CanvasRenderingContext2D, q: Particle) {
  if ((q.dir ?? 1) < 0) g.scale(-1, 1)
  g.rotate(Math.cos((q.t / q.dur!) * TAU) * -0.05)
  drawSleighTeam(g, q.s!, q.t)
}
function batActor(o: { x: number; y: number; vx: number; vy?: number; s: number; life?: number; bob?: number; amb?: boolean }): Particle {
  return P({ kind: 'act', upd: batUpd, drw: batDrw, x: o.x, y: o.y, y0: o.y, vx: o.vx, vy: o.vy ?? 0, s: o.s, fp: R(0, TAU), fs: R(13, 17), ph: R(0, TAU), bob: o.bob ?? R(6, 12), life: o.life, ml: o.life, amb: o.amb })
}
function batUpd(e: Engine, q: Particle, dt: number) {
  q.x += q.vx * dt
  q.y0 = (q.y0 ?? q.y) + q.vy * dt
  q.y = q.y0 + Math.sin(q.t * 2.2 + (q.ph ?? 0)) * (q.bob ?? 8)
  q.fp = (q.fp ?? 0) + (q.fs ?? 15) * dt
  if ((q.vx >= 0 && q.x > e.w + 60) || (q.vx < 0 && q.x < -60)) q.dead = true
}
function batDrw(g: CanvasRenderingContext2D, q: Particle) {
  if (q.vx < 0) g.scale(-1, 1)
  drawBat(g, q.s!, q.fp ?? 0)
}
function witchActor(e: Engine, o: { y0: number; dur: number; s: number }): Particle {
  return P({ kind: 'act', upd: witchUpd, drw: witchDrw, x: -999, y: 0, dir: Math.random() < 0.5 ? 1 : -1, y0: o.y0, s: o.s, dur: o.dur })
}
function witchUpd(e: Engine, q: Particle) {
  const u = q.t / q.dur!, dir = q.dir!
  if (u >= 1) { q.dead = true; return }
  q.x = (dir > 0 ? -60 : e.w + 60) + dir * (e.w + 120) * u
  q.y = q.y0! - Math.sin(u * Math.PI) * 14 * q.s! + Math.sin(q.t * 4) * 1.5
}
function witchDrw(g: CanvasRenderingContext2D, q: Particle) {
  if ((q.dir ?? 1) < 0) g.scale(-1, 1)
  g.rotate(-0.08)
  drawWitch(g, q.s!, q.t)
}

/** Thần Cupid (nhìn sang phải): cánh vỗ, tay giương cung tên đầu tim. */
function drawCupid(g: CanvasRenderingContext2D, s: number, t: number) {
  g.scale(s, s)
  const f = Math.sin(t * 13)
  // cánh sau + cánh trước vỗ ngược pha
  for (const [ox, k, a] of [[-5, 0.85, 0.75], [-2, 1, 1]] as const) {
    g.save(); g.translate(ox, -6); g.rotate(-0.5 + f * 0.45 * k); g.globalAlpha *= a
    g.fillStyle = '#FFFFFF'; g.strokeStyle = 'rgba(214,51,108,.35)'; g.lineWidth = 0.7
    g.beginPath(); g.moveTo(0, 0); g.bezierCurveTo(-4, -12, -14, -14, -17, -8); g.quadraticCurveTo(-12, -6, -14, -2); g.quadraticCurveTo(-8, -2, -9, 2); g.quadraticCurveTo(-4, 1, 0, 0); g.fill(); g.stroke()
    g.restore()
  }
  // chân đạp nhẹ
  g.strokeStyle = '#F2B48F'; g.lineWidth = 3; g.lineCap = 'round'
  const k = Math.sin(t * 5) * 1.5
  g.beginPath(); g.moveTo(-3, 6); g.lineTo(-8, 10 + k); g.moveTo(1, 7); g.lineTo(-2, 12 - k); g.stroke()
  // thân + khăn quấn
  g.fillStyle = '#F8C9A8'; g.beginPath(); g.ellipse(0, 1, 6.5, 7, 0.15, 0, TAU); g.fill()
  g.fillStyle = '#FFB3C8'; g.beginPath(); g.moveTo(-6, 3); g.quadraticCurveTo(0, 9, 6, 2); g.lineTo(6, 6); g.quadraticCurveTo(0, 11, -6, 7); g.closePath(); g.fill()
  // đầu, tóc xoăn vàng, má hồng
  g.fillStyle = '#F8C9A8'; g.beginPath(); g.arc(3, -10, 6, 0, TAU); g.fill()
  g.fillStyle = '#F2B84B'
  for (const [hx, hy, hr] of [[-1, -14, 2.6], [2, -16, 2.8], [6, -15, 2.4], [-2.6, -10.5, 2.2], [8, -12, 1.8]] as const) { g.beginPath(); g.arc(hx, hy, hr, 0, TAU); g.fill() }
  g.fillStyle = '#3B2614'; g.beginPath(); g.arc(6, -10, 0.9, 0, TAU); g.fill()
  g.fillStyle = 'rgba(255,107,139,.55)'; g.beginPath(); g.arc(5.5, -7.2, 1.6, 0, TAU); g.fill()
  // cung + tên
  g.strokeStyle = '#B07A2A'; g.lineWidth = 1.6; g.beginPath(); g.arc(10, 0, 9, -1.15, 1.15); g.stroke()
  g.strokeStyle = 'rgba(80,50,20,.6)'; g.lineWidth = 0.6; g.beginPath(); g.moveTo(13.7, -8.2); g.lineTo(6, 0); g.lineTo(13.7, 8.2); g.stroke()
  g.strokeStyle = '#8A5A1C'; g.lineWidth = 1.1; g.beginPath(); g.moveTo(5, 0); g.lineTo(23, 0); g.stroke()
  g.fillStyle = '#E64980'; g.save(); g.translate(25, 0); g.rotate(Math.PI / 2); heartPath(g, 6); g.fill(); g.restore()
  // tay
  g.strokeStyle = '#F2B48F'; g.lineWidth = 2.4; g.beginPath(); g.moveTo(3, -2); g.lineTo(10, -1); g.moveTo(1, 0); g.lineTo(6.5, 0.5); g.stroke()
}
function cupidActor(e: Engine, o: { y0: number; dur: number; s: number }): Particle {
  return P({ kind: 'act', upd: cupidUpd, drw: cupidDrw, x: -999, y: 0, dir: Math.random() < 0.5 ? 1 : -1, y0: o.y0, s: o.s, dur: o.dur, tr: 0 })
}
function cupidUpd(e: Engine, q: Particle, dt: number) {
  const u = q.t / q.dur!, dir = q.dir!, s = q.s!
  if (u >= 1) { q.dead = true; return }
  q.x = (dir > 0 ? -50 * s : e.w + 50 * s) + dir * (e.w + 100 * s) * u
  q.y = q.y0! + Math.sin(u * TAU * 1.5) * 10 * s + Math.sin(q.t * 6) * 1.5
  q.tr = (q.tr ?? 0) - dt
  if (q.tr <= 0) {
    q.tr = 0.12
    e.p.push(P({ kind: 'spr', spr: pick(S().heart), s: R(5, 9) * s, x: q.x - dir * 12 * s, y: q.y + R(-2, 6) * s, vx: -dir * R(5, 18), vy: R(-14, 6), rot: R(-0.3, 0.3), life: R(0.8, 1.3), ml: 1.3 }))
  }
}
function cupidDrw(g: CanvasRenderingContext2D, q: Particle) {
  if ((q.dir ?? 1) < 0) g.scale(-1, 1)
  drawCupid(g, q.s!, q.t)
}

/** Chim bay (mùa thu): đôi cánh vỗ, nhìn nghiêng xa. */
function drawBird(g: CanvasRenderingContext2D, s: number, flap: number) {
  g.scale(s, s)
  const f = Math.sin(flap)
  g.strokeStyle = '#4A3226'; g.lineWidth = 1.6; g.lineCap = 'round'; g.lineJoin = 'round'
  g.beginPath(); g.moveTo(-9, -2 - f * 5); g.quadraticCurveTo(-4, -3 - f * 2.5, 0, 0); g.quadraticCurveTo(4, -3 - f * 2.5, 9, -2 - f * 5); g.stroke()
  g.fillStyle = '#4A3226'; g.beginPath(); g.ellipse(0, 0.4, 2.2, 1.2, 0, 0, TAU); g.fill()
}
/** Một con trong đàn: x0 = khoảng lùi sau con đầu, y0 đã gồm độ lệch hình chữ V. */
function birdActor(o: { y0: number; dur: number; s: number; dir: number; back: number; delay?: number }): Particle {
  return P({ kind: 'act', upd: birdUpd, drw: birdDrw, x: -999, y: 0, dir: o.dir, y0: o.y0, x0: o.back, s: o.s, dur: o.dur, fp: R(0, TAU), fs: R(8.5, 11), ph: R(0, TAU), delay: o.delay })
}
function birdUpd(e: Engine, q: Particle, dt: number) {
  const u = q.t / q.dur!, dir = q.dir!, s = q.s!
  if (u >= 1) { q.dead = true; return }
  q.x = (dir > 0 ? -40 * s : e.w + 40 * s) + dir * ((e.w + 160 * s) * u - q.x0! * s)
  q.y = q.y0! + Math.sin(q.t * 1.3 + (q.ph ?? 0)) * 3 * s
  q.fp = (q.fp ?? 0) + (q.fs ?? 10) * dt
}
function birdDrw(g: CanvasRenderingContext2D, q: Particle) {
  drawBird(g, q.s!, q.fp ?? 0)
}
/** Đàn chim bay hình chữ V. */
function flock(e: Engine, y0: number, s: number) {
  const dir = Math.random() < 0.5 ? 1 : -1, n = 5 + ((Math.random() * 3) | 0), dur = R(7, 9) * Math.max(1, e.w / 600)
  for (let i = 0; i < n; i++) {
    const k = Math.ceil(i / 2), side = i % 2 ? 1 : -1
    e.p.push(birdActor({ y0: y0 + side * k * 7 * s, dur, s: s * R(0.85, 1), dir, back: k * 16 }))
  }
}
/** Đèn trời: thân giấy cam sáng, lửa lập loè, bay chậm lên rồi mờ dần. */
function drawSkyLantern(g: CanvasRenderingContext2D, s: number, t: number, ph: number) {
  g.scale(s, s)
  const fl = 0.85 + 0.15 * Math.sin(t * 9 + ph)
  const gl = g.createRadialGradient(0, 2, 1, 0, 2, 17); gl.addColorStop(0, `rgba(255,200,90,${(0.55 * fl).toFixed(3)})`); gl.addColorStop(1, 'rgba(255,170,60,0)')
  g.fillStyle = gl; g.beginPath(); g.arc(0, 2, 17, 0, TAU); g.fill()
  const b = g.createLinearGradient(0, -10, 0, 9); b.addColorStop(0, '#FFE8A3'); b.addColorStop(1, '#FF9F43')
  g.fillStyle = b; g.beginPath(); g.moveTo(-6, -9); g.quadraticCurveTo(0, -11.5, 6, -9); g.lineTo(4.6, 8); g.quadraticCurveTo(0, 9.5, -4.6, 8); g.closePath(); g.fill()
  g.strokeStyle = 'rgba(200,110,30,.45)'; g.lineWidth = 0.6; g.beginPath(); g.moveTo(-2.4, -10.4); g.lineTo(-1.8, 8.8); g.moveTo(2.4, -10.4); g.lineTo(1.8, 8.8); g.stroke()
  g.fillStyle = `rgba(255,244,190,${fl.toFixed(3)})`; g.beginPath(); g.ellipse(0, 8.4, 1.6, 2.3 * fl, 0, 0, TAU); g.fill()
}
function skyLanternActor(e: Engine, x: number, s: number): Particle {
  const vy = -R(13, 21) * Math.max(0.6, s), life = (e.h + 50) / -vy
  return P({ kind: 'act', upd: skyLanternUpd, drw: skyLanternDrw, x, x0: x, y: e.h + 22 * s, vy, s, ph: R(0, TAU), life, ml: life })
}
function skyLanternUpd(e: Engine, q: Particle, dt: number) {
  q.y += q.vy * dt
  q.x = q.x0! + Math.sin(q.t * 0.6 + (q.ph ?? 0)) * 8 * q.s!
  if (q.y < -30) q.dead = true
}
function skyLanternDrw(g: CanvasRenderingContext2D, q: Particle) {
  drawSkyLantern(g, q.s!, q.t, q.ph ?? 0)
}

// ── Hạt nền theo dịp: init = rải sẵn khắp màn (khung đầu đã đủ), ngược lại sinh từ mép ──
export const AMB: Partial<Record<SeasonKey, (e: Engine, init: boolean) => Particle>> = {
  tet: (e, init) => {
    const dao = Math.random() < 0.42
    return P({ kind: 'spr', amb: true, dir: 1, spr: dao ? S().dao : S().mai, s: dao ? R(11, 16) : R(14, 21), x: R(0, e.w), y: init ? R(-20, e.h) : R(-60, -20), vx: R(-6, 6), vy: R(20, 38), sa: R(10, 24), sf: R(0.6, 1.3), ph: R(0, TAU), rot: R(0, TAU), vr: R(-1.1, 1.1), fl: dao ? R(1.2, 2.4) : 0, A: R(0.75, 1) })
  },
  thongnhat: (e, init) => P({ kind: 'spr', amb: true, dir: -1, spr: S().star, s: R(6, 11), x: R(0, e.w), y: init ? R(0, e.h + 10) : R(e.h + 6, e.h + 30), vx: R(-3, 3), vy: -R(10, 22), sa: R(4, 10), sf: R(0.6, 1.2), ph: R(0, TAU), rot: R(0, TAU), vr: R(-0.6, 0.6), tw: R(2, 4.5), A: 0.9 }),
  he: (e, init) => {
    const b = Math.random() < 0.82
    return P({ kind: 'spr', amb: true, dir: -1, spr: b ? S().bubble : S().sparkle, s: b ? R(7, 18) : R(8, 12), x: R(0, e.w), y: init ? R(0, e.h + 20) : R(e.h + 10, e.h + 40), vx: R(-3, 3), vy: -R(16, 36), sa: R(5, 12), sf: R(0.8, 1.6), ph: R(0, TAU), vr: b ? 0 : R(-1, 1), tw: b ? 0 : R(2, 4), A: R(0.6, 0.95) })
  },
  thu: (e, init) => {
    const gk = Math.random() < 0.3
    return P({ kind: 'spr', amb: true, dir: 1, spr: pick(gk ? S().ginkgo : S().maple), s: R(15, 24), x: R(0, e.w), y: init ? R(-20, e.h) : R(-60, -24), vx: R(-10, 4), vy: R(26, 48), sa: R(18, 36), sf: R(0.5, 1.1), ph: R(0, TAU), rot: R(0, TAU), vr: R(-1.6, 1.6), fl: R(1.4, 3), A: R(0.8, 1) })
  },
  halloween: (e, init) => {
    const r = Math.random()
    if (r < 0.5) {
      const vx = (Math.random() < 0.5 ? 1 : -1) * R(30, 65)
      return batActor({ x: init ? R(0, e.w) : vx > 0 ? -40 : e.w + 40, y: R(e.h * 0.08, e.h * 0.7), vx, s: R(0.8, 1.3), amb: true })
    }
    if (r < 0.78) return P({ kind: 'spr', amb: true, dir: -1, spr: S().ghost, s: R(16, 24), x: R(0, e.w), y: init ? R(0, e.h + 20) : R(e.h + 10, e.h + 40), vx: R(-4, 4), vy: -R(12, 24), sa: R(8, 16), sf: R(0.6, 1.1), ph: R(0, TAU), rot: R(-0.2, 0.2), A: R(0.55, 0.8) })
    return P({ kind: 'spr', amb: true, dir: -1, spr: pick([S().sparkle, S().sparkleV]), s: R(7, 11), x: R(0, e.w), y: init ? R(0, e.h + 10) : R(e.h + 6, e.h + 30), vx: R(-3, 3), vy: -R(8, 18), sa: R(4, 10), sf: R(0.6, 1.2), ph: R(0, TAU), rot: R(0, TAU), vr: R(-0.6, 0.6), tw: R(2, 4), A: 0.9 })
  },
  // Tuyết dày, hạt to: z = độ gần — hạt gần to hơn, rơi nhanh hơn, rõ hơn
  noel: (e, init) => {
    const z = Math.random(), cr = Math.random() < 0.26
    return P({ kind: 'spr', amb: true, dir: 1, spr: cr ? S().crystal : S().snow, s: cr ? 18 + z * 16 : 8 + z * 16, x: R(0, e.w), y: init ? R(-10, e.h) : R(-40, -12), vx: R(-6, 6), vy: 18 + z * 44, sa: R(6, 16), sf: R(0.4, 1), ph: R(0, TAU), rot: R(0, TAU), vr: cr ? R(-0.8, 0.8) : 0, A: 0.6 + z * 0.4 })
  },
  // Tim bay lên như bóng bay, cánh hồng rơi xuống
  valentine: (e, init) => {
    if (Math.random() < 0.62) return P({ kind: 'spr', amb: true, dir: -1, spr: pick(S().heart), s: R(12, 24), x: R(0, e.w), y: init ? R(0, e.h + 20) : R(e.h + 14, e.h + 44), vx: R(-4, 4), vy: -R(16, 32), sa: R(8, 18), sf: R(0.5, 1.1), ph: R(0, TAU), rot: R(-0.25, 0.25), A: R(0.6, 0.95) })
    return P({ kind: 'spr', amb: true, dir: 1, spr: S().petal, s: R(11, 17), x: R(0, e.w), y: init ? R(-20, e.h) : R(-50, -20), vx: R(-8, 6), vy: R(22, 40), sa: R(14, 28), sf: R(0.5, 1.2), ph: R(0, TAU), rot: R(0, TAU), vr: R(-1.4, 1.4), fl: R(1.4, 2.8), A: R(0.75, 1) })
  },
}

// ── Hiệu ứng khi tạo đơn (down = nút ở trên cùng màn, bung xuống phía nội dung) ──
function burstSpr(e: Engine, ox: number, oy: number, list: readonly HTMLCanvasElement[], n: number, smin: number, smax: number, o: { g?: number; vmin?: number; vmax?: number; down?: boolean } = {}) {
  for (let i = 0; i < n; i++) {
    const a = o.down ? Math.PI / 2 + R(-1.1, 1.1) : -Math.PI / 2 + R(-0.8, 0.8)
    const sp = o.down ? R(160, 460) : R(o.vmin ?? 420, o.vmax ?? 760)
    e.p.push(P({ kind: 'spr', spr: pick(list), s: R(smin, smax), x: ox, y: oy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, g: o.g ?? 1150, drag: 0.982, rot: R(0, TAU), vr: R(-7, 7), fl: R(3, 7), ph: R(0, TAU), life: R(1.8, 2.6), ml: 2.6 }))
  }
}
const CONFETTI = ['#3C70F0', '#685CEE', '#40CDD6', '#FFC94A', '#FF6B6B']
export function burst(key: SeasonKey | null, e: Engine, x: number, y: number, down: boolean) {
  const s = S()
  switch (key) {
    case 'tet': return burstSpr(e, x, y, [s.lixi, s.lixi, s.coin, s.coin, s.chung, s.mai, s.dao], 38, 16, 26, { down })
    case 'valentine': return burstSpr(e, x, y, [...s.heart, ...s.heart, s.petal, s.sparkle], 40, 14, 26, { g: 800, down })
    case 'thongnhat':
      ;([[0.28, 0.2], [0.72, 0.16], [0.5, 0.32]] as const).forEach(([fx, fy], i) => {
        const sx = down ? e.w * fx + R(-40, 40) : x, sy = down ? e.h : y
        const ty = down ? e.h * (fy + 0.1) : Math.min(e.h * fy, y - 120), v = down ? 480 : 340
        const r = rocket(sx, sy, ty, 1, down ? 80 : 64, down ? 1.5 : 1.2)
        r.vy = -v; r.vx = (e.w * fx - sx) / ((sy - ty) / v); r.delay = i * 0.28
        e.p.push(r)
      })
      return
    case 'he':
      if (down) {
        e.p.push(dolphinActor({ x0: e.w * 0.18, x1: e.w * 0.8, wl: e.h - 30, hh: e.h * 0.55, dur: 2.1, s: 1.5 }))
        return burstSpr(e, x, y, [s.bubble, s.drop, s.sparkle], 22, 8, 16, { g: 700, down })
      }
      e.p.push(dolphinActor({ x0: x, x1: x + (x > e.w / 2 ? -1 : 1) * e.w * 0.62, wl: y, hh: e.h * 0.42, dur: 1.9, s: 1.15 }))
      return burstSpr(e, x, y, [s.bubble, s.bubble, s.drop, s.sparkle], 26, 8, 16, { g: 800 })
    case 'thu': return burstSpr(e, x, y, [...s.maple, ...s.ginkgo, s.mooncake, s.mooncake], 36, 16, 26, { g: 700, vmin: 380, vmax: 700, down })
    case 'halloween':
      burstSpr(e, x, y, [...s.candy, s.pumpkin, s.pumpkin, s.ghost], 30, 16, 24, { g: 900, down })
      for (let i = 0; i < 7; i++) {
        const a = (down ? Math.PI / 2 : -Math.PI / 2) + R(-1.2, 1.2), sp = R(120, 220)
        e.p.push(batActor({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp * 0.6, s: R(1, 1.4), life: 2.4, bob: 4 }))
      }
      return
    case 'noel':
      e.p.push(sleighActor(e, { y0: down ? 120 : 150, dur: down ? 4.2 : 3.4, s: down ? 1.4 : 1.05, gifts: true, dir: 1 }))
      return burstSpr(e, x, y, [s.gold, s.gold, s.crystal, s.snow], 26, 10, 20, { g: 900, down })
    default:
      for (let i = 0; i < 70; i++) {
        const a = down ? Math.PI / 2 + R(-1.1, 1.1) : -Math.PI / 2 + R(-0.85, 0.85), sp = down ? R(160, 480) : R(420, 780)
        e.p.push(P({ kind: 'rect', c: pick(CONFETTI), s: R(7, 11), x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, g: 1100, drag: 0.978, rot: R(0, TAU), vr: R(-9, 9), fl: R(4, 9), ph: R(0, TAU), life: R(1.8, 2.6), ml: 2.6 }))
      }
  }
}

// ── Sân khấu: canvas[data-stage] trong băng chúc mừng / cảnh nền ──────
const STAGE: Record<string, Partial<Record<SeasonKey, (e: Engine, dt: number) => void>>> = {
  sea: {
    he: (e, dt) => {
      if (e.dolphins) {
        e.timer2 -= dt
        if (e.timer2 <= 0) { e.timer2 = R(2.2, 4); jumpDolphins(e, Math.random() < 0.45 ? 2 : 1) }
      }
      if (e.surfer && !e.surferOn) {
        e.timer -= dt
        if (e.timer <= 0) { e.surferOn = true; e.p.push(surferActor(e)) }
      }
    },
  },
  sky: {
    noel: (e, dt) => {
      e.timer -= dt
      if (e.timer <= 0) { e.timer = R(6.5, 9); e.p.push(sleighActor(e, { y0: e.h * R(0.28, 0.42), dur: R(5.5, 7) * Math.max(1, e.w / 600), s: e.sc })) }
    },
    thu: (e, dt) => {
      e.timer -= dt
      e.timer2 -= dt
      if (e.timer <= 0) { e.timer = R(7, 10); flock(e, e.h * R(0.16, 0.36), e.sc * R(0.8, 1)) }
      if (e.timer2 <= 0) { e.timer2 = R(2.2, 3.6); e.p.push(skyLanternActor(e, e.w * R(0.42, 0.96), e.sc * R(0.75, 1.1))) }
    },
    valentine: (e, dt) => {
      e.timer -= dt
      if (e.timer <= 0) { e.timer = R(6, 8.5); e.p.push(cupidActor(e, { y0: e.h * R(0.3, 0.5), dur: R(5.5, 7) * Math.max(1, e.w / 600), s: e.sc * 1.15 })) }
    },
    halloween: (e, dt) => {
      e.timer -= dt
      e.timer2 -= dt
      if (e.timer <= 0) { e.timer = R(8, 11); e.p.push(witchActor(e, { y0: e.h * R(0.32, 0.46), dur: R(5, 6.5) * Math.max(1, e.w / 600), s: e.sc * 1.1 })) }
      if (e.timer2 <= 0) {
        e.timer2 = R(3.5, 5.5)
        const dir = Math.random() < 0.5 ? 1 : -1, n = 3 + ((Math.random() * 3) | 0), y = e.h * R(0.2, 0.6)
        for (let i = 0; i < n; i++) e.p.push(batActor({ x: dir > 0 ? -30 - i * 18 : e.w + 30 + i * 18, y: y + R(-14, 14), vx: dir * R(70, 100) * Math.max(1, e.w / 500), s: e.sc * R(0.7, 1) }))
      }
    },
  },
}

/**
 * Dựng bộ máy cho một canvas sân khấu theo data-*:
 *   data-stage = sea | sky, data-wl = mực nước (px tính từ đáy), data-amp = biên độ sóng,
 *   data-sc = tỉ lệ nhân vật, data-dolphins / data-surfer = có cá heo / người lướt sóng.
 * Chạy trước một nhịp để khung đầu tiên đã có nhân vật.
 */
export function setupStage(cv: HTMLCanvasElement, key: SeasonKey, motion: boolean): Engine {
  const e = new Engine(cv)
  e.resize(cv.offsetWidth, cv.offsetHeight)
  const ds = cv.dataset, type = ds.stage ?? ''
  e.wl = Number(ds.wl) || 0
  e.amp = Number(ds.amp) || 0
  e.sc = Number(ds.sc) || 1
  e.dolphins = ds.dolphins === '1'
  e.surfer = ds.surfer === '1'
  e.tick = STAGE[type]?.[key] ?? null
  if (type === 'sea') {
    const a = e.amp || 8
    e.waves = [
      { base: e.wl + a * 2.2, A: a * 0.8, len: 260, speed: 0.9, ph: R(0, TAU), top: 'rgba(0,150,200,.45)', bottom: 'rgba(0,105,165,.6)' },
      { base: e.wl, A: a, len: 200, speed: 1.3, ph: R(0, TAU), top: 'rgba(40,200,232,.6)', bottom: 'rgba(0,135,190,.75)' },
      { base: Math.max(a + 4, e.wl - a * 2.4), A: a * 0.85, len: 150, speed: 1.75, ph: R(0, TAU), top: 'rgba(175,240,250,.72)', bottom: 'rgba(55,185,220,.85)', foam: true },
    ]
    e.timer = 0.3
    e.timer2 = 0.6
  } else e.timer = 0.4
  if (e.tick && e.w > 1) {
    const warm = type === 'sea' ? 1 : motion ? 1.4 : 3
    for (let i = 0; i < warm / 0.033; i++) e.step(0.033)
  }
  e.draw()
  return e
}

// ── Cờ đỏ sao vàng tung bay (30/4), vẽ trên canvas 92×68 (hiển thị 46×34) ──
let flagSrc: HTMLCanvasElement | null = null
export function drawFlag(cv: HTMLCanvasElement, t: number) {
  if (!flagSrc) {
    flagSrc = document.createElement('canvas'); flagSrc.width = 120; flagSrc.height = 80
    const fg = flagSrc.getContext('2d')!
    fg.fillStyle = '#DA251D'; fg.fillRect(0, 0, 120, 80); fg.translate(60, 41); fg.fillStyle = '#FFCD00'; starPath(fg, 25, 10); fg.fill()
  }
  const g = cv.getContext('2d')
  if (!g) return
  g.setTransform(1, 0, 0, 1, 0, 0); g.clearRect(0, 0, cv.width, cv.height); g.setTransform(2, 0, 0, 2, 0, 0)
  g.fillStyle = '#7A5A2E'; g.fillRect(3, 1, 1.8, 33); g.fillStyle = '#E0A100'; g.beginPath(); g.arc(3.9, 1.6, 1.7, 0, TAU); g.fill()
  const fw = 38, fh = 25, W = flagSrc.width, H = flagSrc.height
  for (let i = 0; i < fw; i += 0.5) {
    const k = i / fw, ph = i * 0.32 - t * 5.5, off = Math.sin(ph) * 2.4 * k
    g.drawImage(flagSrc, k * W, 0, (W / fw) * 0.5 + 0.5, H, 5 + i, 3 + off, 0.6, fh)
    const sh = Math.cos(ph) * k
    g.fillStyle = sh > 0 ? `rgba(255,255,255,${(sh * 0.16).toFixed(3)})` : `rgba(0,0,0,${(-sh * 0.2).toFixed(3)})`
    g.fillRect(5 + i, 3 + off, 0.6, fh)
  }
}
